import { Button } from "@/components/ui/button";
import { FieldError, Field, FieldLabel } from "@/components/ui/field";
import { Heading } from "@/components/heading";
import { Input } from "@/components/ui/input";
import { TextLink, Text } from "@/components/text";
import {
	createFileRoute,
	useRouteContext,
	useRouter,
} from "@tanstack/react-router";
import { useForm } from "@tanstack/react-form";
import { z } from "zod";
import { Spinner } from "@/components/ui/spinner";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { AnalyticsEvents, useAnalytics } from "@/utils/analytics";
import { toast } from "sonner";
import { graphql, useMutation } from "react-relay";
import type { esqueciASenha_forgotPasswordMutation } from "./__generated__/esqueciASenha_forgotPasswordMutation.graphql";

export const Route = createFileRoute("/_auth/esqueci-a-senha")({
	component: RouteComponent,
});

function RouteComponent() {
	const { club } = useRouteContext({ from: "__root__" });
	const router = useRouter();

	const { track } = useAnalytics();

	const [forgotPassword] =
		useMutation<esqueciASenha_forgotPasswordMutation>(graphql`
	    mutation esqueciASenha_forgotPasswordMutation($club: String!, $email: String!) {
				forgotPassword(username: $email, club: $club)
			}
	`);

	const form = useForm({
		defaultValues: {
			email: "",
		},
		validators: {
			onChange: z.object({
				email: z.string().email("Email inválido"),
			}),
		},
		onSubmit: async ({ value }) => {
			return forgotPassword({
				variables: {
					club: club?.slug || "",
					email: value.email!,
				},
				onCompleted: (response) => {
					if (response.forgotPassword) {
						track(AnalyticsEvents.AUTH_FORGOT_PASSWORD, {});
						toast.success(
							"Email de recuperação enviado! Verifique sua caixa de entrada.",
						);
						form.reset();
						setTimeout(() => {
							router.navigate({
								to: "/login",
							});
						}, 2000);
					}
				},
				onError: (err) => {
					if (err.message?.includes("not found")) {
						toast.error(
							"Não encontramos uma conta com esse email. Verifique o email digitado.",
						);
					} else {
						toast.error(
							"Não foi possível enviar o email de recuperação. Tente novamente.",
						);
					}
				},
			});
		},
	});

	return (
		<div className="flex justify-center w-full">
			<div className="flex w-full flex-col gap-8 justify-center p-6 max-w-[550px]">
				<div className="size-12">
					<Avatar>
						<AvatarFallback>
							{club?.name.slice(0, 2).toUpperCase()}
						</AvatarFallback>
						<AvatarImage src={club?.logo?.signedUrl} />
					</Avatar>
				</div>

				<div className="flex flex-col gap-1.5">
					<Heading>Recuperar Senha</Heading>

					<Text className="text-left text-muted-foreground">
						Digite seu email abaixo e enviaremos um link para você redefinir sua
						senha.
					</Text>
				</div>

				<form
					className="flex flex-col gap-8"
					onSubmit={(e) => {
						e.preventDefault();
						e.stopPropagation();
						form.handleSubmit();
					}}
				>
					<form.Field name="email">
						{(field) => (
							<Field>
								<FieldLabel htmlFor={field.name}>Email</FieldLabel>
								<Input
									id={field.name}
									name={field.name}
									value={field.state.value}
									onBlur={field.handleBlur}
									onChange={(e) => field.handleChange(e.target.value)}
									type="email"
									placeholder="Digite seu email"
									aria-invalid={
										!!(field.state.meta.isTouched && field.state.meta.errors[0])
									}
								/>

								{field.state.meta.isTouched && field.state.meta.errors[0] ? (
									<FieldError errors={field.state.meta.errors} />
								) : null}
							</Field>
						)}
					</form.Field>

					<form.Subscribe
						selector={(state) => [state.isSubmitting, state.isSubmitSuccessful]}
					>
						{([isSubmitting, isSubmitSuccessful]) => (
							<div className="flex flex-col gap-6">
								<Button
									type="submit"
									color={isSubmitSuccessful ? "emerald" : "primary"}
									disabled={isSubmitting || isSubmitSuccessful}
									className="flex items-center justify-center gap-2"
								>
									Enviar email de recuperação{" "}
									{isSubmitting ? <Spinner /> : null}
								</Button>

								<Text className="font-medium">
									Lembrou sua senha?{" "}
									<TextLink to="/login" disabled={isSubmitting}>
										Voltar para login
									</TextLink>
								</Text>
							</div>
						)}
					</form.Subscribe>
				</form>
			</div>
		</div>
	);
}
